/**
 * FINALIZE_WORKSPACE action - Commit, push, and create PR for workspace changes
 *
 * Completes a coding task by committing changes, pushing to remote,
 * and optionally creating a pull request.
 *
 * @module actions/finalize-workspace
 */

import type {
  Action,
  ActionResult,
  HandlerCallback,
  HandlerOptions,
  IAgentRuntime,
  Memory,
  State,
} from "@elizaos/core";
import type { CodingWorkspaceService } from "../services/workspace-service.js";

export const finalizeWorkspaceAction: Action = {
  name: "FINALIZE_WORKSPACE",

  similes: ["COMMIT_AND_PR", "CREATE_PR", "SUBMIT_CHANGES", "FINISH_WORKSPACE"],

  description:
    "Finalize workspace changes by committing, pushing, and optionally creating a pull request. " +
    "Use after a coding agent completes its task.",

  examples: [
    [
      {
        name: "{{user1}}",
        content: { text: "Create a PR for the changes" },
      },
      {
        name: "{{agentName}}",
        content: {
          text: "I'll commit and create a pull request.",
          action: "FINALIZE_WORKSPACE",
        },
      },
    ],
    [
      {
        name: "{{user1}}",
        content: { text: "Submit the coding agent's work as a PR" },
      },
      {
        name: "{{agentName}}",
        content: {
          text: "Finalizing the workspace and creating PR.",
          action: "FINALIZE_WORKSPACE",
        },
      },
    ],
  ],

  validate: async (
    runtime: IAgentRuntime,
    _message: Memory,
  ): Promise<boolean> => {
    const workspaceService = runtime.getService(
      "CODING_WORKSPACE_SERVICE",
    ) as unknown as CodingWorkspaceService | undefined;
    return workspaceService != null;
  },

  handler: async (
    runtime: IAgentRuntime,
    message: Memory,
    state?: State,
    _options?: HandlerOptions,
    callback?: HandlerCallback,
  ): Promise<ActionResult | undefined> => {
    const workspaceService = runtime.getService(
      "CODING_WORKSPACE_SERVICE",
    ) as unknown as CodingWorkspaceService | undefined;
    if (!workspaceService) {
      if (callback) {
        await callback({
          text: "Workspace Service is not available.",
        });
      }
      return { success: false, error: "SERVICE_UNAVAILABLE" };
    }

    const content = message.content as {
      workspaceId?: string;
      commitMessage?: string;
      prTitle?: string;
      prBody?: string;
      baseBranch?: string;
      draft?: boolean;
      skipPR?: boolean;
    };

    // Get workspace ID from content or state
    let workspaceId = content.workspaceId;
    if (!workspaceId && state?.codingWorkspace) {
      workspaceId = (state.codingWorkspace as { id: string }).id;
    }

    if (!workspaceId) {
      const workspaces = workspaceService.listWorkspaces();
      if (workspaces.length === 0) {
        if (callback) {
          await callback({
            text: "No workspaces available. Provision a workspace first.",
          });
        }
        return { success: false, error: "NO_WORKSPACE" };
      }
      workspaceId = workspaces[workspaces.length - 1].id;
    }

    const workspace = workspaceService.getWorkspace(workspaceId);
    if (!workspace) {
      if (callback) {
        await callback({
          text: `Workspace ${workspaceId} not found.`,
        });
      }
      return { success: false, error: "WORKSPACE_NOT_FOUND" };
    }

    try {
      // Check workspace status
      const status = await workspaceService.getStatus(workspaceId);

      if (status.clean && status.staged.length === 0) {
        if (callback) {
          await callback({
            text: "No changes to commit in this workspace.",
          });
        }
        return {
          success: true,
          text: "No changes to commit",
          data: { workspaceId, status },
        };
      }

      // Commit changes
      const commitMessage =
        content.commitMessage ??
        `feat: automated changes from coding agent\n\nGenerated by Milady coding agent plugin.`;

      const commitHash = await workspaceService.commit(workspaceId, {
        message: commitMessage,
        all: true,
      });

      // Push to remote
      await workspaceService.push(workspaceId, { setUpstream: true });

      // Create PR if not skipped
      let prInfo = null;
      if (!content.skipPR) {
        const prTitle = content.prTitle ?? `[Milady] ${workspace.branch}`;
        const prBody =
          content.prBody ??
          `## Summary\n\nAutomated changes generated by Milady coding agent.\n\n` +
            `**Branch:** ${workspace.branch}\n` +
            `**Commit:** ${commitHash}\n\n` +
            `---\n*Generated by @elizaos/plugin-agent-orchestrator*`;

        prInfo = await workspaceService.createPR(workspaceId, {
          title: prTitle,
          body: prBody,
          base: content.baseBranch,
          draft: content.draft,
        });
      }

      if (callback) {
        if (prInfo) {
          await callback({
            text:
              `Workspace finalized!\n` +
              `Commit: ${commitHash.slice(0, 8)}\n` +
              `PR #${prInfo.number}: ${prInfo.url}`,
          });
        } else {
          await callback({
            text:
              `Workspace changes committed and pushed.\n` +
              `Commit: ${commitHash.slice(0, 8)}`,
          });
        }
      }

      return {
        success: true,
        text: prInfo
          ? `Created PR #${prInfo.number}`
          : "Changes committed and pushed",
        data: {
          workspaceId,
          commitHash,
          pr: prInfo ? { number: prInfo.number, url: prInfo.url } : undefined,
        },
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      if (callback) {
        await callback({
          text: `Failed to finalize workspace: ${errorMessage}`,
        });
      }
      return { success: false, error: "FINALIZE_FAILED" };
    }
  },

  parameters: [
    {
      name: "workspaceId",
      description:
        "ID of the workspace to finalize. Uses current workspace if not specified.",
      required: false,
      schema: { type: "string" as const },
    },
    {
      name: "commitMessage",
      description: "Commit message for the changes.",
      required: false,
      schema: { type: "string" as const },
    },
    {
      name: "prTitle",
      description: "Title for the pull request.",
      required: false,
      schema: { type: "string" as const },
    },
    {
      name: "prBody",
      description: "Body/description for the pull request.",
      required: false,
      schema: { type: "string" as const },
    },
    {
      name: "baseBranch",
      description: "Base branch for the PR (e.g., main, develop).",
      required: false,
      schema: { type: "string" as const },
    },
    {
      name: "draft",
      description: "Create as draft PR.",
      required: false,
      schema: { type: "boolean" as const },
    },
    {
      name: "skipPR",
      description: "Skip PR creation, only commit and push.",
      required: false,
      schema: { type: "boolean" as const },
    },
  ],
};
